import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useCallback, useState } from "react";
import { formatString } from "../../../../functions/functions";
import { TravelReportDataType } from "../../../../types/types";

interface AllTripsMapLayerProps {
    trips: TravelReportDataType[];
    selectedTrip: TravelReportDataType | null;
    vehicleType?: string | null;
    /** Viagem para a qual o Google Directions foi solicitado */
    approxTrip?: TravelReportDataType | null;
}

// ─── Cores por viagem (fallback por índice) ──────────────────────────────────
const TRIP_COLORS = [
    "#0FB403", "#1565c0", "#e65100", "#6a1b9a",
    "#00838f", "#ad1457", "#37474f", "#558b2f",
    "#4527a0", "#bf360c",
];

// ─── Cache estático para DirectionsService (rota aproximada) ─────────────────
let approxRouteCache = new Map<string, any>();
export const clearApproxRouteCache = () => approxRouteCache.clear();

// ─── Haversine simples ────────────────────────────────────────────────────────
const distanceMetres = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── Ícone do veículo ─────────────────────────────────────────────────────────
const getVehicleIcon = (type: string | null | undefined, size = 32): any => {
    const g = (window as any).google;
    if (!g?.maps) return null;
    try {
        const formatted = formatString(type || "carro");
        const iconPath = require(`../../../../assets/files/cars/png/${formatted}.png`);
        return {
            url: iconPath,
            scaledSize: new g.maps.Size(size, size),
            anchor: new g.maps.Point(size / 2, size / 2),
        };
    } catch {
        try {
            return {
                url: require(`../../../../assets/files/cars/png/carro.png`),
                scaledSize: new g.maps.Size(size, size),
                anchor: new g.maps.Point(size / 2, size / 2),
            };
        } catch {
            return null;
        }
    }
};

// ─── Formata duração em HH:mm:ss ─────────────────────────────────────────────
const fmtDuration = (minutes: number): string => {
    const s = Math.round(minutes * 60);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

// ─── InfoWindow HTML ──────────────────────────────────────────────────────────
const buildInfoWindowContent = (
    trip: TravelReportDataType,
    isStart: boolean,
    color: string,
    tripIndex: number
): string => {
    const title = isStart ? "INÍCIO DA VIAGEM" : "FIM DA VIAGEM";
    const addr = isStart ? (trip.startAddress || "Endereço não identificado") : (trip.endAddress || "Endereço não identificado");
    const time = isStart ? trip.startTime : trip.endTime;

    const hasName = trip.driverName && trip.driverName !== "null";
    const hasId = trip.driverUniqueId && trip.driverUniqueId !== "null";
    const driver = hasName && hasId
        ? `${trip.driverName} (${trip.driverUniqueId})`
        : hasName ? trip.driverName
            : hasId ? trip.driverUniqueId
                : "iButton ausente";

    const dist = Number(trip.distance || 0).toFixed(1);
    const dur = fmtDuration(Number(trip.duration || 0));

    return `
        <div style="padding:12px;font-family:'Roboto',sans-serif;min-width:230px;max-width:280px;">
            <b style="color:${color};font-size:13px;display:block;margin-bottom:6px;
                border-bottom:1px solid #eee;padding-bottom:4px;">
                VIAGEM #${tripIndex + 1} — ${title}
            </b>
            <p style="margin:4px 0;font-size:12px;color:#333;line-height:1.4;">
                <b>Endereço:</b> ${addr}
            </p>
            <p style="margin:4px 0;font-size:12px;color:#333;">
                <b>${isStart ? "Início" : "Fim"}:</b> ${time || "—"}
            </p>
            <p style="margin:4px 0;font-size:12px;color:#333;">
                <b>Motorista:</b> ${driver}
            </p>
            <p style="margin:4px 0;font-size:12px;color:#333;">
                <b>Distância:</b> ${dist} km &nbsp;|&nbsp; <b>Duração:</b> ${dur}
            </p>
        </div>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const AllTripsMapLayer = ({
    trips,
    selectedTrip,
    vehicleType,
    approxTrip = null,
}: AllTripsMapLayerProps) => {
    const map = useMap();
    const routesLibrary = useMapsLibrary("routes");

    // Refs para todos os objetos Google Maps criados
    const polylinesRef = useRef<any[]>([]);
    const markersRef = useRef<any[]>([]);
    const infoWindowsRef = useRef<any[]>([]);
    const approxRendererRef = useRef<any>(null);

    // State para controlar se o renderer de rota aproximada está inicializado
    const [directionsService, setDirectionsService] = useState<any>(null);

    // ── Inicializa DirectionsService quando a biblioteca estiver pronta ───────
    useEffect(() => {
        if (!routesLibrary || !map) return;
        const svc = new routesLibrary.DirectionsService();
        setDirectionsService(svc);

        const renderer = new routesLibrary.DirectionsRenderer({
            map,
            suppressMarkers: true,
            draggable: false,
            preserveViewport: true,
            polylineOptions: {
                strokeColor: "#ff6f00",
                strokeWeight: 5,
                strokeOpacity: 0.85,
            },
        });
        approxRendererRef.current = renderer;

        return () => {
            renderer.setMap(null);
        };
    }, [routesLibrary, map]);

    // ── Cleanup geral ─────────────────────────────────────────────────────────
    const cleanupAll = useCallback(() => {
        polylinesRef.current.forEach(p => p.setMap(null));
        markersRef.current.forEach(m => m.setMap(null));
        infoWindowsRef.current.forEach(iw => iw.close());
        polylinesRef.current = [];
        markersRef.current = [];
        infoWindowsRef.current = [];
        if (approxRendererRef.current) {
            approxRendererRef.current.setMap(null);
        }
    }, []);

    // ── Desenha todos os trajetos ─────────────────────────────────────────────
    useEffect(() => {
        if (!map || !trips.length) return;
        const g = (window as any).google;
        if (!g?.maps) return;

        cleanupAll();

        const bounds = new g.maps.LatLngBounds();
        const icon = getVehicleIcon(vehicleType, 30);

        // Fechar todos os InfoWindows ao clicar no mapa
        const mapClickListener = map.addListener("click", () => {
            infoWindowsRef.current.forEach(iw => iw.close());
        });

        trips.forEach((trip, idx) => {
            const isSelected = selectedTrip === trip;
            const isAnySelected = selectedTrip !== null;
            const color = TRIP_COLORS[idx % TRIP_COLORS.length];

            // ── Opacidade: selecionada = 1, não selecionada quando há seleção = 0.2
            const opacity = isAnySelected ? (isSelected ? 1.0 : 0.2) : 0.85;
            const weight = isSelected ? 5 : (isAnySelected ? 2 : 3);

            const startPos = { lat: trip.startLat, lng: trip.startLng };
            const endPos = { lat: trip.endLat, lng: trip.endLng };

            const dist = distanceMetres(trip.startLat, trip.startLng, trip.endLat, trip.endLng);
            const isValidCoords =
                trip.startLat && trip.startLng && trip.endLat && trip.endLng
                && Math.abs(trip.startLat) <= 90 && Math.abs(trip.endLat) <= 90;

            if (!isValidCoords) return;

            // ── Polyline GPS direta ───────────────────────────────────────────
            if (dist >= 10) {
                const polyline = new g.maps.Polyline({
                    path: [startPos, endPos],
                    map,
                    geodesic: true,
                    strokeColor: isSelected ? "#0FB403" : color,
                    strokeOpacity: opacity,
                    strokeWeight: weight,
                    zIndex: isSelected ? 10 : 1,
                });
                polylinesRef.current.push(polyline);
            }

            bounds.extend(startPos);
            bounds.extend(endPos);

            // ── Marcador de INÍCIO ────────────────────────────────────────────
            const startMarker = new g.maps.Marker({
                position: startPos,
                map,
                title: `Viagem #${idx + 1} — Início`,
                icon: icon ?? {
                    path: g.maps.SymbolPath.CIRCLE,
                    scale: 7,
                    fillColor: "#0FB403",
                    fillOpacity: opacity,
                    strokeColor: "#fff",
                    strokeWeight: 2,
                },
                opacity: isAnySelected ? (isSelected ? 1 : 0.35) : 1,
                zIndex: isSelected ? 20 : 5,
            });

            // ── Marcador de FIM ───────────────────────────────────────────────
            const endMarker = new g.maps.Marker({
                position: endPos,
                map,
                title: `Viagem #${idx + 1} — Fim`,
                icon: {
                    path: g.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: "#f44336",
                    fillOpacity: opacity,
                    strokeColor: "#fff",
                    strokeWeight: 2,
                },
                opacity: isAnySelected ? (isSelected ? 1 : 0.35) : 1,
                zIndex: isSelected ? 20 : 5,
            });

            // ── InfoWindow de INÍCIO ──────────────────────────────────────────
            const startIW = new g.maps.InfoWindow({
                content: buildInfoWindowContent(trip, true, "#0FB403", idx),
                disableAutoPan: true,
            });

            // ── InfoWindow de FIM ─────────────────────────────────────────────
            const endIW = new g.maps.InfoWindow({
                content: buildInfoWindowContent(trip, false, "#f44336", idx),
                disableAutoPan: true,
            });

            startMarker.addListener("click", () => {
                infoWindowsRef.current.forEach(iw => iw.close());
                startIW.open(map, startMarker);
            });

            endMarker.addListener("click", () => {
                infoWindowsRef.current.forEach(iw => iw.close());
                endIW.open(map, endMarker);
            });

            markersRef.current.push(startMarker, endMarker);
            infoWindowsRef.current.push(startIW, endIW);

            // ── Abre InfoWindow da viagem selecionada (ou a 1ª se nenhuma) ────
            if (isSelected || (!isAnySelected && idx === 0)) {
                setTimeout(() => {
                    infoWindowsRef.current.forEach(iw => iw.close());
                    startIW.open(map, startMarker);
                }, 500);
            }
        });

        // ── Ajusta câmera ─────────────────────────────────────────────────────
        if (selectedTrip) {
            // fitBounds apenas na viagem selecionada
            const selBounds = new g.maps.LatLngBounds();
            selBounds.extend({ lat: selectedTrip.startLat, lng: selectedTrip.startLng });
            selBounds.extend({ lat: selectedTrip.endLat, lng: selectedTrip.endLng });
            map.fitBounds(selBounds, 210);
        } else if (!bounds.isEmpty()) {
            map.fitBounds(bounds, 200);
        }

        return () => {
            g.maps.event.removeListener(mapClickListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, trips, selectedTrip, vehicleType]);

    // ── Rota Aproximada (Google Directions) — sob demanda ────────────────────
    useEffect(() => {
        const renderer = approxRendererRef.current;
        if (!approxTrip || !directionsService || !renderer || !map) {
            renderer?.setMap(null);
            return;
        }

        const { startLat, startLng, endLat, endLng, startTime } = approxTrip;
        const dist = distanceMetres(startLat, startLng, endLat, endLng);

        if (dist < 50) {
            renderer.setMap(null);
            return;
        }

        const cacheKey = `approx-${startLat},${startLng}-${endLat},${endLng}-${startTime}`;

        if (approxRouteCache.has(cacheKey)) {
            renderer.setMap(map);
            renderer.setDirections(approxRouteCache.get(cacheKey));
            return;
        }

        renderer.setMap(map);

        directionsService
            .route({
                origin: { lat: startLat, lng: startLng },
                destination: { lat: endLat, lng: endLng },
                travelMode: (window as any).google?.maps?.TravelMode?.DRIVING || "DRIVING",
                optimizeWaypoints: false,
                provideRouteAlternatives: false,
            })
            .then((response: any) => {
                approxRouteCache.set(cacheKey, response);
                renderer.setDirections(response);
            })
            .catch((err: any) => {
                console.warn("AllTripsMapLayer: rota aproximada falhou.", err?.message ?? err);
                renderer.setMap(null);
            });
    }, [approxTrip, directionsService, map]);

    // ── Cleanup ao desmontar ──────────────────────────────────────────────────
    useEffect(() => {
        return () => cleanupAll();
    }, [cleanupAll]);

    return null;
};

export default AllTripsMapLayer;
